import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { ArrowBack, Visibility } from "@material-ui/icons";
import {
  CustomTable,
  DateRangePicker,
  ModalError,
  ModalSuccess,
  PrimaryButton,
  SelectInput,
  SkeletonComponent,
  StatusChip,
} from "components";
import { getTotalPage } from "lib";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getErrors } from "utils";
import {
  getAdminPotentialClaimDSPs,
  getAdminPotentialClaimDSRFReports,
  getAdminPotentialClaimDSRFReportDetail,
  processAdminPotentialClaim,
} from "utils/DSRFService";

const useStyles = makeStyles(() => ({
  actionButton: {
    backgroundColor: "#111827",
    color: "#FFFFFF",
    borderRadius: 6,
    width: 36,
    height: 36,
    padding: 6,
    "&:hover": {
      backgroundColor: "#1f2937",
    },
  },
  actionIcon: {
    fontSize: 18,
    color: "#FFFFFF",
  },
  dialogPaper: {
    borderRadius: 8,
    padding: "8px 0 16px 0",
  },
  dialogTitle: {
    paddingBottom: 0,
    paddingTop: 16,
    paddingLeft: 24,
    paddingRight: 24,
  },
  dialogContent: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 16,
  },
  backButton: {
    marginRight: 8,
    padding: 4,
    color: "#111827",
  },
  divider: {
    borderTop: "1px solid #E5E7EB",
    marginTop: 16,
    marginBottom: 20,
  },
  summaryContainer: {
    border: "1px solid #D1D5DC",
    borderRadius: 8,
    padding: 16,
    marginTop: 20,
  },
  summaryCardNeutral: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #D1D5DC",
    borderRadius: 8,
    padding: 16,
    height: "100%",
  },
  summaryCardSuccess: {
    backgroundColor: "#F0FDF4",
    border: "1px solid #BBF7D0",
    borderRadius: 8,
    padding: 16,
    height: "100%",
  },
  summaryCardDanger: {
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: 16,
    height: "100%",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 12,
  },
  cardLabel: {
    fontSize: 13,
    color: "#374151",
    marginBottom: 4,
  },
  cardValue: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 12,
  },
  cardFooterText: {
    fontSize: 12,
    color: "#6B7280",
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 12,
  },
  progressText: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    marginBottom: 6,
  },
  progressBarTrack: {
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    height: 8,
    width: "100%",
    overflow: "hidden",
  },
  progressBarFill: {
    backgroundColor: "#111827",
    height: "100%",
    borderRadius: 4,
  },
}));

const statusTypeMap = {
  open: "danger",
  "in progress": "warning",
  in_progress: "warning",
  "on progress": "warning",
  on_progress: "warning",
  running: "warning",
  r: "warning",
  q: "warning",
  queued: "warning",
  finish: "success",
  finished: "success",
  success: "success",
  failed: "danger",
  error: "danger",
};

const statusLabelMap = {
  open: "Open",
  "in progress": "In Progress",
  in_progress: "In Progress",
  "on progress": "In Progress",
  on_progress: "In Progress",
  running: "In Progress",
  r: "In Progress",
  q: "Queued",
  queued: "Queued",
  finish: "Finish",
  finished: "Finish",
  success: "Finish",
  failed: "Failed",
  error: "Error",
};

const getStatusLabel = status => {
  if (!status) return "-";
  const key = String(status).toLowerCase();
  if (statusLabelMap[key]) return statusLabelMap[key];
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
};

const getStatusType = status => {
  if (!status) return "default";
  const key = String(status).toLowerCase();
  return statusTypeMap[key] || "default";
};

const getItemId = item =>
  item?.key ?? item?.id ?? item?.report_id ?? item?.dsrf_report_id;


const formatNumber = num => {
  if (num === null || num === undefined || num === "") return "0";
  const parsed = Number(String(num).replace(/[^0-9.-]+/g, ""));
  return isNaN(parsed) ? num : parsed.toLocaleString("en-US");
};

const formatCurrency = val => {
  if (val === null || val === undefined || val === "") return "Rp0";
  if (typeof val === "string" && val.startsWith("Rp")) return val;
  const parsed = Number(String(val).replace(/[^0-9.-]+/g, ""));
  if (isNaN(parsed)) return val;
  return `Rp${parsed.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const ComponentPotentialClaimMatching = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dspOptions, setDspOptions] = useState([]);
  const [selectedDsp, setSelectedDsp] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const [openDetail, setOpenDetail] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Reference to track the latest request and prevent race condition (stale responses overwriting fresh filter data)
  const latestRequestIdRef = useRef(0);

  const fetchDSPs = async () => {
    try {
      const res = await getAdminPotentialClaimDSPs({ page: 1, size: 50 });
      const rawData = res?.data?.data || [];
      const list = rawData.map(item => ({
        ...item,
        id: item?.id ?? item?.dsp_id,
        dsp_name: item?.dsp_name ?? item?.name ?? item?.folder_name ?? "",
      }));
      setDspOptions(list);
    } catch (error) {
      ModalError(getErrors(error?.response || error));
    }
  };

  const fetchReports = useCallback(
    async (isBackground = false) => {
      if (!selectedDsp) {
        setReportsData([]);
        setTableTotalPage(1);
        return;
      }

      const currentRequestId = ++latestRequestIdRef.current;

      if (!isBackground) {
        setLoading(true);
      }
      try {
        const params = {
          page: queryParams.page,
          size: queryParams.size,
          dsp_id: selectedDsp,
          ...(startDate
            ? { start_date: moment(startDate).format("YYYY-MM-DD") }
            : {}),
          ...(endDate
            ? { end_date: moment(endDate).format("YYYY-MM-DD") }
            : {}),
          ...(queryParams.search ? { search: queryParams.search } : {}),
        };
        const res = await getAdminPotentialClaimDSRFReports(params);

        // Ignore stale response if a newer request has already been initiated
        if (currentRequestId !== latestRequestIdRef.current) {
          return;
        }

        const data = res?.data?.data || [];
        const meta = res?.data?.meta;
        const total =
          meta?.total ??
          meta?.total_data ??
          meta?.total_items ??
          data?.length ??
          0;

        setReportsData(data);
        const pageCount = getTotalPage(total, queryParams.size);
        setTableTotalPage(pageCount || 1);
      } catch (error) {
        if (currentRequestId !== latestRequestIdRef.current) {
          return;
        }
        if (!isBackground) {
          ModalError(getErrors(error?.response || error));
          setReportsData([]);
          setTableTotalPage(1);
        }
      } finally {
        if (currentRequestId === latestRequestIdRef.current && !isBackground) {
          setLoading(false);
        }
      }
    },
    [selectedDsp, startDate, endDate, queryParams]
  );

  useEffect(() => {
    fetchDSPs();
  }, []);

  // Fetch data with 200ms debounce to prevent multiple calls when DateRangePicker sets startDate & endDate sequentially
  useEffect(() => {
    if (!selectedDsp) {
      setReportsData([]);
      setTableTotalPage(1);
      return;
    }

    const timer = setTimeout(() => {
      fetchReports(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [fetchReports, selectedDsp]);

  // Smart Polling: only poll background updates when there are items currently running / in progress
  useEffect(() => {
    const hasActiveProgress = reportsData.some(item => {
      const status = String(item?.status || "").toLowerCase();
      return [
        "r",
        "running",
        "in progress",
        "in_progress",
        "on progress",
        "on_progress",
        "q",
        "queued",
      ].includes(status);
    });

    if (!hasActiveProgress || !selectedDsp) return;

    const interval = setInterval(() => {
      fetchReports(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [reportsData, selectedDsp, fetchReports]);

  // Detail Modal Polling: only poll when detail modal is open and the process is still running
  useEffect(() => {
    if (!openDetail || !detailData?.key) return;

    const statusKey = String(
      detailData?.status || detailData?.matching_status || ""
    ).toLowerCase();
    const isProcessing = [
      "in progress",
      "in_progress",
      "on progress",
      "on_progress",
      "running",
      "queued",
      "q",
      "r",
    ].includes(statusKey);

    if (!isProcessing) return;

    const interval = setInterval(async () => {
      try {
        const res = await getAdminPotentialClaimDSRFReportDetail(detailData.key);
        const fetchedData = res?.data?.data || res?.data;
        if (fetchedData) {
          setDetailData(prev => ({
            ...prev,
            ...fetchedData,
          }));
        }
      } catch (error) {
        // silent fail on background poll
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [openDetail, detailData?.key, detailData?.status, detailData?.matching_status]);


  const handleChangeQueryParams = (value, key) => {
    setQueryParams(prev => ({
      ...prev,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const selectableRows = useMemo(
    () =>
      reportsData.filter(
        item => (item?.status || "").toString().toLowerCase() === "open"
      ),
    [reportsData]
  );

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelectedRows(selectableRows.map(item => getItemId(item)));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (e, item) => {
    if ((item?.status || "").toString().toLowerCase() !== "open") return;
    const id = getItemId(item);
    if (e.target.checked) {
      setSelectedRows(prev => [...prev, id]);
    } else {
      setSelectedRows(prev => prev.filter(rowId => rowId !== id));
    }
  };

  const handleViewDetail = async item => {
    if (!item) return;
    const key = getItemId(item);
    setDetailData(item);
    setOpenDetail(true);
    setLoadingDetail(true);
    try {
      const res = await getAdminPotentialClaimDSRFReportDetail(key);
      const fetchedData = res?.data?.data || res?.data || {};
      setDetailData(prev => ({
        ...prev,
        ...fetchedData,
      }));
    } catch (error) {
      ModalError(getErrors(error?.response || error));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setDetailData(null);
  };

  const handleProcessMatch = async () => {
    if (selectedRows.length === 0) return;
    setProcessing(true);
    try {
      const selectedItems = reportsData.filter(item =>
        selectedRows.includes(getItemId(item))
      );
      const keys = selectedItems.map(item => item?.key).filter(Boolean);

      const payload = {
        keys: keys.length > 0 ? keys : selectedRows,
      };
      const res = await processAdminPotentialClaim(payload);
      ModalSuccess(
        res?.data?.message ||
          "Potential Claim Matching process has been started successfully."
      );
      setSelectedRows([]);
      await fetchReports(false);
    } catch (error) {
      ModalError(getErrors(error?.response || error));
    } finally {
      setProcessing(false);
    }
  };


  const tableColumns = [
    {
      name: "dsp_name",
      title: "DSP",
      renderText: (dspName, item) =>
        dspName || item?.dsp || item?.dsp_folder_name || "-",
    },
    {
      name: "date_period",
      title: "Data Period",
      renderText: (datePeriod, item) => {
        if (datePeriod) return datePeriod;
        if (item?.data_period) return item.data_period;
        if (item?.start_date && item?.end_date) {
          return `${moment(item.start_date).format("D MMM YYYY")} - ${moment(
            item.end_date
          ).format("D MMM YYYY")}`;
        }
        return item?.period || "-";
      },
    },
    {
      name: "status",
      title: "Status",
      renderText: (status, item) => {
        const currentStatus = item?.status || status || "-";
        return (
          <StatusChip
            label={getStatusLabel(currentStatus)}
            type={getStatusType(currentStatus)}
          />
        );
      },
    },
  ];

  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every(item => selectedRows.includes(getItemId(item)));

  const detailStatus = detailData?.status || "-";
  const detailStatusKey = String(detailStatus).toLowerCase();
  const detailStatusType = getStatusType(detailStatus);
  const detailStatusLabel = getStatusLabel(detailStatus);


  const detailPeriodDate =
    detailData?.date_period ||
    detailData?.data_period ||
    (detailData?.start_date && detailData?.end_date
      ? `${moment(detailData.start_date).format("DD/MM/YYYY")} - ${moment(
          detailData.end_date
        ).format("DD/MM/YYYY")}`
      : detailData?.period || "-");

  const isFinished = ["finish", "finished", "success"].includes(
    detailStatusKey
  );

  const isInProgress = [
    "in progress",
    "in_progress",
    "running",
    "on progress",
    "on_progress",
    "r",
  ].includes(detailStatusKey);

  const progressPercentage =
    detailData?.progress_percentage ??
    detailData?.progress ??
    detailData?.percentage ??
    0;


  const summaryObj = detailData?.summary || {};
  const totalData =
    summaryObj?.total_data ||
    detailData?.total_data ||
    detailData?.total_dsrf_data ||
    {};
  const totalMatched =
    summaryObj?.total_matched ||
    detailData?.total_matched ||
    detailData?.matched_data ||
    {};
  const totalUnmatched =
    summaryObj?.total_unmatched ||
    detailData?.total_unmatched ||
    detailData?.unmatched_data ||
    {};

  const summaryCards = [
    {
      title: "Total DSRF Data",
      revenue: formatCurrency(
        totalData?.total_revenue ?? totalData?.revenue ?? 0
      ),
      traffic: formatNumber(
        totalData?.total_traffic ?? totalData?.traffic ?? 0
      ),
      songs:
        totalData?.total_songs !== undefined || totalData?.songs !== undefined
          ? `${formatNumber(totalData?.total_songs ?? totalData?.songs)} Songs`
          : null,
      records:
        totalData?.total_records !== undefined ||
        totalData?.records !== undefined
          ? `${formatNumber(
              totalData?.total_records ?? totalData?.records
            )} Records`
          : null,
      cardClass: classes.summaryCardNeutral,
    },
    {
      title: "Total DSRF Matched",
      revenue: formatCurrency(
        totalMatched?.total_revenue ?? totalMatched?.revenue ?? 0
      ),
      traffic: formatNumber(
        totalMatched?.total_traffic ?? totalMatched?.traffic ?? 0
      ),
      songs:
        totalMatched?.total_songs !== undefined ||
        totalMatched?.songs !== undefined
          ? `${formatNumber(
              totalMatched?.total_songs ?? totalMatched?.songs
            )} Songs`
          : null,
      records:
        totalMatched?.total_records !== undefined ||
        totalMatched?.records !== undefined
          ? `${formatNumber(
              totalMatched?.total_records ?? totalMatched?.records
            )} Records`
          : null,
      cardClass: classes.summaryCardSuccess,
    },
    {
      title: "Total Unmatched Data",
      revenue: formatCurrency(
        totalUnmatched?.total_revenue ?? totalUnmatched?.revenue ?? 0
      ),
      traffic: formatNumber(
        totalUnmatched?.total_traffic ?? totalUnmatched?.traffic ?? 0
      ),
      songs:
        totalUnmatched?.total_songs !== undefined ||
        totalUnmatched?.songs !== undefined
          ? `${formatNumber(
              totalUnmatched?.total_songs ?? totalUnmatched?.songs
            )} Songs`
          : null,
      records:
        totalUnmatched?.total_records !== undefined ||
        totalUnmatched?.records !== undefined
          ? `${formatNumber(
              totalUnmatched?.total_records ?? totalUnmatched?.records
            )} Records`
          : null,
      cardClass: classes.summaryCardDanger,
    },
  ];


  return (
    <Grid container spacing={3}>
      {/* Filter Section */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <SelectInput
              label="DSP"
              value={selectedDsp}
              onChange={({ target }) => {
                setSelectedDsp(target.value || "");
                setQueryParams(prev => ({ ...prev, page: 1 }));
                setSelectedRows([]);
              }}
              options={dspOptions}
              optionKey="id"
              optionLabel="dsp_name"
              placeholder="Select DSP"
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DateRangePicker
              label="Data Period"
              startDate={startDate}
              endDate={endDate}
              handleChangeStartDate={val => {
                setStartDate(val);
                setQueryParams(prev => ({ ...prev, page: 1 }));
                setSelectedRows([]);
              }}
              handleChangeEndDate={val => {
                setEndDate(val);
                setQueryParams(prev => ({ ...prev, page: 1 }));
                setSelectedRows([]);
              }}
              placeholder="All"
              clear
              width="100%"
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Table Section */}
      <Grid item xs={12}>
        {loading ? (
          <SkeletonComponent variant="wave" height={400} />
        ) : (
          <CustomTable
            columnTable={tableColumns}
            data={reportsData}
            isLoading={loading}
            isHaveCheckbox
            handleCheckboxHeader={handleSelectAll}
            checkboxHeaderChecked={allSelected}
            disabledCheckboxHeaderChecked={selectableRows.length === 0}
            disabledCheckbox={item =>
              (item?.status || "").toString().toLowerCase() !== "open"
            }
            checked={item => selectedRows.includes(getItemId(item))}
            handleCheckbox={handleSelectRow}
            isHaveAction
            renderAction={item => (
              <IconButton
                className={classes.actionButton}
                onClick={() => handleViewDetail(item)}
                size="small"
              >
                <Visibility className={classes.actionIcon} />
              </IconButton>
            )}
            page={queryParams.page}
            pageSize={queryParams.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
          />
        )}
      </Grid>

      {/* Action Button Section */}
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <PrimaryButton
            label={processing ? "Processing..." : "Process Match"}
            disabled={selectedRows.length === 0 || processing}
            onClick={handleProcessMatch}
          />
        </Box>
      </Grid>

      {/* Detail Dialog */}
      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        maxWidth={isFinished || isInProgress ? "md" : "sm"}
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Grid container alignItems="center">
            <Grid item>
              <IconButton
                onClick={handleCloseDetail}
                size="small"
                className={classes.backButton}
              >
                <ArrowBack />
              </IconButton>
            </Grid>
            <Grid item>
              <Box fontSize={18} fontWeight={700} color="#111827">
                Detail DSRF Matching 2
              </Box>
            </Grid>
          </Grid>
          <Box fontSize={14} color="#6B7280" mt={0.5} ml={4.5}>
            This is detail information of Potential Claim Match
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.divider} />
          {loadingDetail ? (
            <SkeletonComponent variant="wave" height={180} />
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box mb={2}>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      DSP
                    </Box>
                    <Box fontSize={14} mt={0.5} color="#111827">
                      {detailData?.dsp_name ||
                        detailData?.dsp ||
                        detailData?.dsp_folder_name ||
                        "-"}
                    </Box>
                  </Box>
                  <Box>
                    <Box fontSize={14} fontWeight={700} color="#111827" mb={0.5}>
                      Status
                    </Box>
                    <StatusChip
                      label={detailStatusLabel}
                      type={detailStatusType}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Box fontSize={14} fontWeight={700} color="#111827">
                      Period Date
                    </Box>
                    <Box fontSize={14} mt={0.5} color="#111827">
                      {detailPeriodDate}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {isInProgress && (
                <>
                  <Box className={classes.divider} />
                  <Box mt={1}>
                    <Typography className={classes.progressTitle}>
                      Potential Claim Match Progress
                    </Typography>
                    <Typography className={classes.progressText}>
                      {progressPercentage}%
                    </Typography>
                    <Box className={classes.progressBarTrack}>
                      <Box
                        className={classes.progressBarFill}
                        width={`${Math.min(
                          Math.max(progressPercentage, 0),
                          100
                        )}%`}
                      />
                    </Box>
                  </Box>
                </>
              )}

              {isFinished && (
                <>
                  <Box className={classes.divider} />
                  <Box className={classes.summaryContainer}>
                    <Grid container spacing={2}>
                      {summaryCards.map((card, idx) => (
                        <Grid item xs={12} sm={4} md={4} key={idx}>
                          <Box className={card.cardClass}>
                            <Box className={classes.cardTitle}>{card.title}</Box>

                            <Box className={classes.cardLabel}>Total Revenue</Box>
                            <Box className={classes.cardValue}>{card.revenue}</Box>

                            <Box className={classes.cardLabel}>Total Traffic</Box>
                            <Box className={classes.cardValue}>{card.traffic}</Box>

                            {card.songs && (
                              <Box className={classes.cardFooterText} mb={0.5}>
                                {card.songs}
                              </Box>
                            )}
                            {card.records && (
                              <Box className={classes.cardFooterText}>
                                {card.records}
                              </Box>
                            )}

                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </>
              )}

            </>
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default ComponentPotentialClaimMatching;
